import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { AddNewProspectModal } from '../../features/add-new-prospect/AddNewProspectModal'
import type { CreatedProspect } from '../../features/add-new-prospect/prospectClient'
import { SetReminderModal } from '../../features/reminders/SetReminderModal'
import { ReviewMissingInformationModal } from '../../features/review-missing-information/ReviewMissingInformationModal'
import { ScriptModal } from '../../features/scripts/ScriptModal'
import { AddSalespersonModal } from '../../features/staff/AddSalespersonModal'
import { DashboardActionQueueModal, type DashboardReviewQueue } from '../../features/review-missing-information/DashboardActionQueueModal'
import type { CrmDataState } from '../../hooks/useCrmData'
import { MobileHeader } from './MobileHeader'
import { MobileNav } from './MobileNav'
import { Sidebar } from './Sidebar'

function dashboardQueueFromLabel(label: string): DashboardReviewQueue | null {
  switch (label) {
    case 'Missing Owners':
      return 'missing-owner'
    case 'Missing Contact Info':
      return 'missing-contact'
    case 'Missing Website':
      return 'missing-website'
    case 'Missing Location':
      return 'missing-location'
    case 'Missing Notes':
      return 'missing-notes'
    default:
      return null
  }
}
type AppShellProps = {
  children: ReactNode
  dataState: CrmDataState
}

export function AppShell({ children, dataState }: AppShellProps) {
  const [addProspectOpen, setAddProspectOpen] = useState(false)
  const [reminderOpen, setReminderOpen] = useState(false)
  const [reminderTrigger, setReminderTrigger] = useState<HTMLButtonElement | null>(null)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [reviewQueue, setReviewQueue] = useState<DashboardReviewQueue | null>(null)
  const [reviewTrigger, setReviewTrigger] = useState<HTMLButtonElement | null>(null)
  const [salespersonTrigger, setSalespersonTrigger] = useState<HTMLButtonElement | null>(null)
  const [scriptOpen, setScriptOpen] = useState(false)
  const [scriptTrigger, setScriptTrigger] = useState<HTMLButtonElement | null>(null)
  const [successNotice, setSuccessNotice] = useState('')
  const addProspectTriggerRef = useRef<HTMLButtonElement | null>(null)
  const restoreAddProspectFocusRef = useRef(false)
  const addProspectFocusFrameRef = useRef<number | null>(null)
  const noticeTimerRef = useRef<number | null>(null)

  useEffect(() => () => {
    if (addProspectFocusFrameRef.current !== null) window.cancelAnimationFrame(addProspectFocusFrameRef.current)
    if (noticeTimerRef.current !== null) window.clearTimeout(noticeTimerRef.current)
  }, [])

  useEffect(() => {
    if (addProspectOpen || !restoreAddProspectFocusRef.current) return
    restoreAddProspectFocusRef.current = false
    const trigger = addProspectTriggerRef.current
    addProspectFocusFrameRef.current = window.requestAnimationFrame(() => {
      addProspectFocusFrameRef.current = null
      if (trigger?.isConnected) trigger.focus({ preventScroll: true })
    })
  }, [addProspectOpen])

  useEffect(() => {
  function handleDashboardReview(event: Event) {
    const customEvent = event as CustomEvent<{
      trigger?: HTMLButtonElement
      label?: string
    }>

    const queue = dashboardQueueFromLabel(customEvent.detail?.label ?? '')

    if (!queue) return

    setAddProspectOpen(false)
    setReviewQueue(queue)

    if (customEvent.detail?.trigger) {
      setReviewTrigger(customEvent.detail.trigger)
    }

    setReviewOpen(true)
  }

  window.addEventListener('wolf-forge-dashboard-review', handleDashboardReview)

  return () => {
    window.removeEventListener('wolf-forge-dashboard-review', handleDashboardReview)
  }
}, [])
  useEffect(() => {
    function handleCrmRefresh() {
      dataState.refresh({ cacheBust: true })
    }

    window.addEventListener(
      'wolf-forge-crm-refresh',
      handleCrmRefresh,
    )

    return () => {
      window.removeEventListener(
        'wolf-forge-crm-refresh',
        handleCrmRefresh,
      )
    }
  }, [dataState.refresh])
function openAddProspect(trigger: HTMLButtonElement) {
    if (addProspectFocusFrameRef.current !== null) {
      window.cancelAnimationFrame(addProspectFocusFrameRef.current)
      addProspectFocusFrameRef.current = null
    }
    restoreAddProspectFocusRef.current = false
    addProspectTriggerRef.current = trigger
    setReviewOpen(false)
    setAddProspectOpen(true)
  }

  const closeAddProspect = useCallback(() => {
    restoreAddProspectFocusRef.current = true
    setAddProspectOpen(false)
  }, [])

  function showNotice(message: string) {
    setSuccessNotice(message)
    if (noticeTimerRef.current !== null) window.clearTimeout(noticeTimerRef.current)
    noticeTimerRef.current = window.setTimeout(() => {
      noticeTimerRef.current = null
      setSuccessNotice('')
    }, 5_000)
  }

  function confirmCreatedProspect(prospect: CreatedProspect) {
    dataState.mergeConfirmedProspect(prospect)
    dataState.refresh({ cacheBust: true })
    showNotice('Prospect added to Google Sheets')
  }

  function openAddSalesperson(trigger: HTMLButtonElement) {
    setAddProspectOpen(false)
    setReminderOpen(false)
    setReviewOpen(false)
    setReviewQueue(null)
    setScriptOpen(false)
    setSalespersonTrigger(trigger)
  }

  function closeAddSalesperson() {
    const trigger = salespersonTrigger
    setSalespersonTrigger(null)
    window.requestAnimationFrame(() => {
      if (trigger?.isConnected) trigger.focus({ preventScroll: true })
    })
  }


  function openReminder(trigger: HTMLButtonElement) {
    setAddProspectOpen(false)
    setReviewOpen(false)
    setReviewQueue(null)
    setReminderTrigger(trigger)
    setReminderOpen(true)
  }

  function closeReminder() {
    setReminderOpen(false)

    window.requestAnimationFrame(() => {
      if (reminderTrigger?.isConnected) {
        reminderTrigger.focus({
          preventScroll: true,
        })
      }
    })
  }
  function openReview(trigger: HTMLButtonElement) {
    setAddProspectOpen(false)
    setReviewQueue(null)
    setReviewTrigger(trigger)
    setReviewOpen(true)
  }

  function closeReview() {
    setReviewOpen(false)
    setReviewQueue(null)
    window.requestAnimationFrame(() => reviewTrigger?.focus())
  }

  function openScript(trigger: HTMLButtonElement) {
    setAddProspectOpen(false)
    setReminderOpen(false)
    setReviewOpen(false)
    setReviewQueue(null)

    setScriptTrigger(trigger)
    setScriptOpen(true)
  }

  function closeScript() {
    setScriptOpen(false)

    window.requestAnimationFrame(() => {
      if (scriptTrigger?.isConnected) {
        scriptTrigger.focus({
          preventScroll: true,
        })
      }
    })
  }
  return (
    <>
      <div className="app-shell">
        <Sidebar onAddNewProspect={openAddProspect} onAddSalesperson={openAddSalesperson} onSetReminder={openReminder} onReviewMissingInformation={openReview} onOpenScript={openScript} />
        <div className="workspace">
          <MobileHeader />
          <header className="desktop-topbar"></header>
          <main className="main-canvas">{children}</main>
        </div>
        <MobileNav onAddNewProspect={openAddProspect} onAddSalesperson={openAddSalesperson} onSetReminder={openReminder} onReviewMissingInformation={openReview} onOpenScript={openScript} />
      </div>
      {addProspectOpen ? (
        <AddNewProspectModal
          onClose={closeAddProspect}
          onCreated={confirmCreatedProspect}
          organizations={dataState.data?.organizations ?? []}
        />
      ) : null}
      {reminderOpen && dataState.data ? (
        <SetReminderModal
          dataset={dataState.data}
          onClose={closeReminder}
        />
      ) : null}
      {salespersonTrigger ? (
        <AddSalespersonModal
          onAdded={(staff) => showNotice(`${staff.displayName} added to the sales team`)}
          onClose={closeAddSalesperson}
        />
      ) : null}
      {scriptOpen ? (
        <ScriptModal onClose={closeScript} />
      ) : null}
      {reviewOpen && dataState.data ? (
        reviewQueue ? (
          <DashboardActionQueueModal
            dataset={dataState.data}
            onClose={closeReview}
            onUpdated={dataState.refresh}
            queue={reviewQueue}
          />
        ) : (
          <ReviewMissingInformationModal
            dataset={dataState.data}
            onClose={closeReview}
            onSelectQueue={setReviewQueue}
          />
        )
      ) : null}
      {successNotice ? <div className="prospect-success-toast" role="status">{successNotice}</div> : null}
    </>
  )
}










